import { useCallback, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

// helper
import { sanitizeObject } from '../../helpers/sanitize.helper';

export interface QueryParams {
    [key: string]: string | number | boolean | undefined;
}

const parseQueryString = (queryString: string): QueryParams => {
    const params = new URLSearchParams(queryString);
    const queryParams: QueryParams = {};

    params.forEach((value, key) => {
        queryParams[key] = value;
    });

    return queryParams;
};

const objectToQueryString = (params: QueryParams): string => {
    const queryString = new URLSearchParams();

    Object.keys(params).forEach((key) => {
        const value = params[key];
        if (value !== undefined) {
            queryString.set(key, value.toString());
        }
    });

    return queryString.toString();
};

const useHandleQueryParams = (): [
    QueryParams,
    (newParams: QueryParams, override?: boolean) => void,
    (keys: string[]) => void,
    () => void,
] => {
    const history = useHistory();
    const location = useLocation();
    const [queryParams, setQueryParams] = useState<QueryParams>(
        parseQueryString(location.search),
    );

    useEffect(() => {
        setQueryParams(parseQueryString(location.search));
    }, [location.search]);

    const updateRoute = useCallback(
        (search: string) => {
            history.replace({
                pathname: location.pathname,
                search,
                state: location.state, // carry forward previous state
            });
        },
        [history, location.pathname, location.state],
    );

    const updateQueryParams = useCallback(
        (newParams: QueryParams, override: boolean = false) => {
            setQueryParams((prevParams) => {
                let mergedParams = override
                    ? sanitizeObject(newParams)
                    : sanitizeObject({
                          ...prevParams,
                          ...newParams,
                      });
                const queryString = objectToQueryString(mergedParams);
                updateRoute(queryString);
                return mergedParams;
            });
        },
        [updateRoute],
    );

    const removeQueryParams = useCallback(
        (keys: string[]) => {
            setQueryParams((prevParams) => {
                const currentParams = { ...prevParams };
                keys.forEach((key) => delete currentParams[key]);
                const queryString = objectToQueryString(currentParams);
                updateRoute(queryString);
                return currentParams;
            });
        },
        [updateRoute],
    );

    const clearParams = useCallback(() => {
        setQueryParams({});
        updateRoute('');
    }, [updateRoute]);

    return [queryParams, updateQueryParams, removeQueryParams, clearParams];
};

export default useHandleQueryParams;
